import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { getCentralToday, getCentralMonth } from "@/utils/dateHelpers";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Bug, Plus, Trash2, Loader2, Calendar,
  FileCheck2, Archive, Download, CheckCircle2,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function PestControlTab({ rooms = [] }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterMonth, setFilterMonth] = useState(getCentralMonth());
  const [adding, setAdding] = useState(false);

  // Cycle state
  const [cycle, setCycle] = useState(null);
  const [cycles, setCycles] = useState([]);
  const [closing, setClosing] = useState(false);

  // New visit form
  const [visitDate, setVisitDate] = useState(getCentralToday());
  const [selectedRooms, setSelectedRooms] = useState([]);
  const [notes, setNotes] = useState("");

  const roomNumbers = rooms
    .map(r => r.room_number || r.roomNumber || r)
    .sort((a, b) => String(a).localeCompare(String(b), undefined, { numeric: true }));

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/pest-control?month=${filterMonth}`);
      setLogs(res.data || []);
    } catch { toast.error("Failed to load pest control logs"); }
    finally { setLoading(false); }
  }, [filterMonth]);

  const fetchCycle = useCallback(async () => {
    try {
      const [c, h] = await Promise.all([
        axios.get(`${API}/admin/pest-control/current-cycle`),
        axios.get(`${API}/admin/pest-control/cycles`),
      ]);
      setCycle(c.data);
      setCycles(h.data || []);
    } catch {
      // silent — feature optional
    }
  }, []);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);
  useEffect(() => { fetchCycle(); }, [fetchCycle]);

  const toggleRoom = (room) => {
    setSelectedRooms(prev => prev.includes(room) ? prev.filter(r => r !== room) : [...prev, room]);
  };
  const selectAll = () => setSelectedRooms([...roomNumbers]);
  const clearAll = () => setSelectedRooms([]);

  const handleAdd = async () => {
    if (selectedRooms.length === 0) { toast.error("Select at least one room"); return; }
    setAdding(true);
    try {
      await axios.post(`${API}/admin/pest-control`, { date: visitDate, rooms: selectedRooms, notes });
      toast.success(`Logged ${selectedRooms.length} rooms sprayed`);
      setSelectedRooms([]);
      setNotes("");
      await Promise.all([fetchLogs(), fetchCycle()]);
    } catch { toast.error("Failed to add record"); }
    finally { setAdding(false); }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/admin/pest-control/${id}`);
      toast.success("Record deleted");
      await Promise.all([fetchLogs(), fetchCycle()]);
    } catch { toast.error("Failed to delete"); }
  };

  const handleCloseCycle = async () => {
    if (!cycle?.is_complete) {
      toast.error(`Cannot close — ${cycle?.remaining_rooms?.length || 0} rooms still pending`);
      return;
    }
    if (!window.confirm(`Close this pest control cycle?\n\n${cycle.sprayed_count} of ${cycle.total_rooms} rooms sprayed across ${(cycle.visits || []).length} visits. A PDF report will be archived and a new cycle will begin.`)) return;
    setClosing(true);
    try {
      const res = await axios.post(`${API}/admin/pest-control/close-cycle`);
      toast.success(`Cycle #${res.data.cycle_number} archived as PDF`);
      await fetchCycle();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to close cycle");
    } finally {
      setClosing(false);
    }
  };

  const handleDownloadPdf = (cycleId) => {
    window.open(`${API}/admin/pest-control/cycles/${cycleId}/pdf`, "_blank");
  };

  const sprayedSet = new Set((cycle?.sprayed_rooms || []).map(r => String(r.room_number)));
  const progressPct = cycle?.total_rooms ? Math.round((cycle.sprayed_count / cycle.total_rooms) * 100) : 0;

  return (
    <>
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-outfit text-xl text-vault-text flex items-center gap-2" data-testid="pest-control-title">
          <Bug className="w-5 h-5 text-vault-gold" /> Pest Control Log
        </h2>
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-vault-text-secondary" />
          <Input type="month" value={filterMonth} onChange={e => setFilterMonth(e.target.value)}
            className="w-44 bg-vault-surface border-vault-border text-vault-text" data-testid="pest-filter-month" />
        </div>
      </div>

      {/* ── Current Cycle Status ─────────────────────────────────────── */}
      {cycle && (
        <Card className="bg-vault-surface border-vault-border mb-6" data-testid="pest-cycle-status">
          <CardHeader className="border-b border-vault-border pb-3 flex flex-row items-center justify-between">
            <CardTitle className="font-outfit text-sm text-vault-text flex items-center gap-2">
              <FileCheck2 className="w-4 h-4 text-vault-gold" /> Current Cycle
            </CardTitle>
            <Button
              size="sm"
              onClick={handleCloseCycle}
              disabled={!cycle.is_complete || closing}
              data-testid="pest-close-cycle-btn"
              className={cycle.is_complete
                ? "bg-emerald-500 text-black hover:bg-emerald-400"
                : "bg-vault-surface-highlight text-vault-text-secondary cursor-not-allowed"}
            >
              {closing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Archive className="w-4 h-4 mr-2" />}
              {cycle.is_complete ? "Close Cycle & Generate PDF" : `${cycle.remaining_rooms?.length || 0} rooms to go`}
            </Button>
          </CardHeader>
          <CardContent className="p-4">
            <div className="flex items-baseline justify-between mb-1">
              <span className="text-vault-text-secondary text-xs">Progress</span>
              <span className="text-vault-gold font-bold text-sm" data-testid="pest-cycle-progress-label">
                {cycle.sprayed_count} / {cycle.total_rooms} rooms &nbsp;·&nbsp; {progressPct}% &nbsp;·&nbsp; {(cycle.visits || []).length} visits
              </span>
            </div>
            <div className="w-full h-2 rounded bg-black/40 overflow-hidden mb-3">
              <div
                className={`h-full transition-all ${cycle.is_complete ? "bg-emerald-400" : "bg-vault-gold"}`}
                style={{ width: `${progressPct}%` }}
                data-testid="pest-cycle-progress-bar"
              />
            </div>
            <div className="flex flex-wrap gap-1.5">
              {roomNumbers.map(room => {
                const isSprayed = sprayedSet.has(String(room));
                return (
                  <div
                    key={room}
                    data-testid={`pest-cycle-room-${room}`}
                    className={`px-2.5 py-1 rounded text-xs font-mono border ${
                      isSprayed
                        ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-300"
                        : "bg-amber-500/10 border-amber-500/30 text-amber-300"
                    }`}
                    title={isSprayed ? "Sprayed this cycle" : "Pending"}
                  >
                    {isSprayed && <CheckCircle2 className="w-3 h-3 inline mr-1" />}
                    {room}
                  </div>
                );
              })}
            </div>
            {cycle.started_at && cycle.started_at !== "1970-01-01T00:00:00+00:00" && (
              <p className="text-vault-text-secondary text-[11px] mt-3">
                Cycle started {new Date(cycle.started_at).toLocaleDateString()}
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* New Visit Form */}
      <Card className="bg-vault-surface border-vault-border mb-6" data-testid="pest-add-form">
        <CardHeader className="border-b border-vault-border pb-3">
          <CardTitle className="font-outfit text-sm text-vault-text">Log New Pest Control Visit</CardTitle>
        </CardHeader>
        <CardContent className="p-4 space-y-4">
          <div className="flex items-center gap-4">
            <div>
              <label className="text-vault-text-secondary text-xs block mb-1">Date of Treatment</label>
              <Input type="date" value={visitDate} onChange={e => setVisitDate(e.target.value)}
                className="w-44 bg-black/30 border-vault-border text-vault-text" data-testid="pest-visit-date" />
            </div>
            <div className="flex-1">
              <label className="text-vault-text-secondary text-xs block mb-1">Notes</label>
              <Input value={notes} onChange={e => setNotes(e.target.value)} placeholder="e.g. Monthly spray, treatment type..."
                className="bg-black/30 border-vault-border text-vault-text" data-testid="pest-notes" />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-vault-text-secondary text-xs">Rooms Sprayed ({selectedRooms.length}/{roomNumbers.length})</label>
              <div className="flex gap-2">
                <button onClick={selectAll} className="text-xs text-vault-gold hover:underline" data-testid="pest-select-all">Select All</button>
                <button onClick={clearAll} className="text-xs text-red-400 hover:underline" data-testid="pest-clear-all">Clear</button>
              </div>
            </div>
            <div className="flex flex-wrap gap-2" data-testid="pest-room-grid">
              {roomNumbers.map(room => (
                <button key={room} onClick={() => toggleRoom(room)} data-testid={`pest-room-${room}`}
                  className={`px-3 py-1.5 rounded text-xs font-medium transition-all ${
                    selectedRooms.includes(room)
                      ? 'bg-vault-gold text-black'
                      : 'bg-black/30 text-vault-text-secondary border border-vault-border hover:border-vault-gold/50'
                  }`}>
                  {room}
                </button>
              ))}
            </div>
          </div>

          <Button onClick={handleAdd} disabled={adding || selectedRooms.length === 0} data-testid="pest-add-btn"
            className="bg-vault-gold text-black hover:bg-vault-gold/90">
            {adding ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
            Log Visit ({selectedRooms.length} rooms)
          </Button>
        </CardContent>
      </Card>

      {/* Cycle History */}
      {cycles.length > 0 && (
        <Card className="bg-vault-surface border-vault-border mb-6" data-testid="pest-cycle-history">
          <CardHeader className="border-b border-vault-border pb-3">
            <CardTitle className="font-outfit text-sm text-vault-text flex items-center gap-2">
              <Archive className="w-4 h-4 text-vault-gold" /> Cycle History ({cycles.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="pest-cycle-history-table">
                <thead className="bg-vault-surface-highlight border-b border-vault-border">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-vault-gold uppercase tracking-wider">Cycle #</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-vault-gold uppercase tracking-wider">Date Range</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-vault-gold uppercase tracking-wider">Rooms</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-vault-gold uppercase tracking-wider">PDF</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-vault-border">
                  {cycles.map(c => (
                    <tr key={c.id} className="hover:bg-vault-surface-highlight/50" data-testid={`pest-cycle-row-${c.cycle_number}`}>
                      <td className="px-4 py-3 text-sm font-bold text-vault-gold">#{c.cycle_number}</td>
                      <td className="px-4 py-3 text-sm text-vault-text">
                        {c.started_at?.slice(0, 10)} → {c.closed_at?.slice(0, 10)}
                      </td>
                      <td className="px-4 py-3 text-sm text-vault-text-secondary">
                        {c.sprayed_count} / {c.total_rooms}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Button size="sm" variant="ghost"
                          onClick={() => handleDownloadPdf(c.id)}
                          className="text-vault-gold hover:text-vault-text"
                          data-testid={`pest-download-pdf-${c.cycle_number}`}>
                          <Download className="w-4 h-4 mr-1" /> PDF
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Visit History */}
      <Card className="bg-vault-surface border-vault-border">
        <CardHeader className="border-b border-vault-border pb-3">
          <CardTitle className="font-outfit text-sm text-vault-text">Visit History — {filterMonth}</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-vault-gold" /></div>
          ) : logs.length === 0 ? (
            <div className="p-8 text-center text-vault-text-secondary text-sm">No pest control visits logged for this month</div>
          ) : (
            <div className="divide-y divide-vault-border">
              {logs.map(log => (
                <div key={log.id} className="p-4 hover:bg-vault-surface-highlight/50" data-testid={`pest-log-${log.id}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-vault-gold font-medium text-sm">{log.date}</span>
                        <span className="bg-vault-gold/20 text-vault-gold px-2 py-0.5 rounded text-xs font-medium">
                          {log.room_count || log.rooms?.length || 0} rooms sprayed
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-1.5 mb-1">
                        {(log.rooms || []).map(r => (
                          <span key={r} className="bg-black/40 text-vault-text-secondary px-2 py-0.5 rounded text-xs">{r}</span>
                        ))}
                      </div>
                      {log.notes && <p className="text-vault-text-secondary text-xs mt-1">{log.notes}</p>}
                    </div>
                    <button onClick={() => handleDelete(log.id)} className="text-red-400 hover:text-red-300 p-1" data-testid={`pest-delete-${log.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </>
  );
}
